// ============================================================================
// NotificationsBell — sininho com painel de notificações de conquistas
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { Bell, Trophy, X } from 'lucide-react';
import { useAchievements } from '../contexts/AchievementsContext.jsx';
import { RANK_STYLES, RANK_NAMES, ACHIEVEMENT_CATEGORIES } from '../data/achievements.js';
import { formatCurrency } from '../utils/formatters.js';

function formatRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `há ${d}d`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

function getCategoryById(role, categoryId) {
  return (ACHIEVEMENT_CATEGORIES[role] || []).find(c => c.id === categoryId);
}

export default function NotificationsBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAchievements();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-white/5 transition-colors"
        title="Notificações"
      >
        <Bell size={18} className="text-[#8C9CB5] hover:text-[#E9EEF5]" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DE4F15] text-white text-[9px] font-bold flex items-center justify-center"
            style={{ animation: 'achievementPulse 2s ease-in-out infinite' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-2xl shadow-2xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-gradient-to-r from-[#F8FAFC] to-white">
            <div className="flex items-center gap-2">
              <Trophy size={14} className="text-gold" />
              <h3 className="font-bold text-sm text-ink">Conquistas</h3>
              {unreadCount > 0 && <span className="text-[10px] bg-[#DE4F15] text-white px-1.5 py-0.5 rounded-full">{unreadCount} novas</span>}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllAsRead} className="text-[10px] text-slate-700 hover:text-gold transition-colors">
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Trophy size={28} className="mx-auto text-[#CBD5E1] mb-2" />
                <p className="text-sm text-slate-500">Nenhuma conquista ainda.</p>
                <p className="text-[11px] text-slate-400 mt-1">No fechamento do mês as conquistas aparecem aqui.</p>
              </div>
            ) : notifications.map(n => {
              const cat = getCategoryById(n.role, n.categoryId);
              const Icon = cat?.icon || Trophy;
              const style = RANK_STYLES[n.tierRank] || RANK_STYLES.bronze;
              const fmt = (v) => cat?.format === 'currency' ? formatCurrency(v) : String(v);
              const unread = !n.readAt;
              return (
                <button
                  key={n.id}
                  onClick={() => unread && markAsRead(n.id)}
                  className={`w-full text-left px-4 py-3 border-b border-[#E9EEF5] last:border-b-0 hover:bg-slate-50 transition-colors flex items-start gap-3 ${unread ? 'bg-gold/5' : ''}`}
                >
                  {/* Mini badge */}
                  <div
                    className="rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 38, height: 38,
                      background: style.gradient,
                      boxShadow: style.glow,
                    }}
                  >
                    <Icon size={18} style={{ color: style.text, filter: `drop-shadow(0 0 4px ${style.color})` }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-xs text-ink">Tier {n.tier + 1}</span>
                      <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: style.color }}>{RANK_NAMES[n.tierRank] || ''}</span>
                      {unread && <span className="ml-auto w-1.5 h-1.5 bg-[#DE4F15] rounded-full" />}
                    </div>
                    <div className="text-xs text-slate-700 mt-0.5">
                      <strong>{n.categoryLabel}</strong>: {fmt(n.tierValue)}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {formatRelative(n.createdAt)} · referente ao mês {n.monthKey}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
